import { isTerminalWriter } from './callPolicy';
import type { CallTerminalOutcome, CallTerminalRecord } from './callState';

export interface CallRoomParticipants {
  localUserId: string;
  peerUserId?: string;
}

export type CallProtocolEvent =
  | {
      kind: 'ring';
      roomId: string;
      eventId: string;
      senderId: string;
      originServerTs: number;
      callId: string;
      peerId: string;
    }
  | {
      kind: 'terminal';
      roomId: string;
      eventId: string;
      senderId: string;
      originServerTs: number;
      record: CallTerminalRecord;
    };

interface MatrixCallEventInput {
  type: string;
  roomId: string;
  eventId: string;
  senderId: string;
  originServerTs: number;
  content: unknown;
}

interface ParseOptions {
  now: number;
  directRoomIds: Set<string>;
}

const callClockSkewToleranceMs = 5_000;
const callIdPattern = /^gbacall_[A-Za-z0-9_-]{8,}$/;
const matrixIdPattern = /^@[^:]+:.+$/;
const outcomes = new Set<CallTerminalOutcome>([
  'completed',
  'declined',
  'cancelled',
  'missed',
  'busy',
  'failed',
]);
const callRecordFields = new Set([
  'call_id',
  'outcome',
  'initiator',
  'started_ts',
  'answered_ts',
  'ended_ts',
  'duration_sec',
]);
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNonNegative = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

export const isCallId = (value: unknown): value is string =>
  typeof value === 'string' && callIdPattern.test(value);

export const buildRingContent = (callId: string, peerId: string) => {
  if (!isCallId(callId) || !matrixIdPattern.test(peerId)) {
    throw new Error('Invalid call ring');
  }
  return {
    application: 'm.call' as const,
    notify_type: 'ring' as const,
    call_id: callId,
    'm.mentions': { user_ids: [peerId], room: false },
  };
};

export const parseCallRecordContent = (
  content: unknown,
): CallTerminalRecord | undefined => {
  if (
    !isRecord(content) ||
    Object.keys(content).some((field) => !callRecordFields.has(field))
  )
    return undefined;
  const outcome = content.outcome;
  if (
    !isCallId(content.call_id) ||
    typeof outcome !== 'string' ||
    !outcomes.has(outcome as CallTerminalOutcome) ||
    typeof content.initiator !== 'string' ||
    !matrixIdPattern.test(content.initiator) ||
    !isFiniteNonNegative(content.started_ts) ||
    !isFiniteNonNegative(content.ended_ts) ||
    content.ended_ts < content.started_ts
  ) {
    return undefined;
  }
  const answeredTs = content.answered_ts;
  const durationSec = content.duration_sec;
  const hasAnsweredFields =
    answeredTs !== undefined && durationSec !== undefined;
  const forbidsAnsweredFields = [
    'declined',
    'cancelled',
    'missed',
    'busy',
  ].includes(outcome);
  if (
    (answeredTs !== undefined &&
      (!isFiniteNonNegative(answeredTs) ||
        answeredTs < content.started_ts ||
        answeredTs > content.ended_ts)) ||
    (durationSec !== undefined &&
      (!isFiniteNonNegative(durationSec) || !Number.isInteger(durationSec))) ||
    (answeredTs === undefined) !== (durationSec === undefined) ||
    (outcome === 'completed' && !hasAnsweredFields) ||
    (forbidsAnsweredFields && hasAnsweredFields) ||
    (hasAnsweredFields &&
      durationSec !== Math.floor((content.ended_ts - answeredTs) / 1000))
  ) {
    return undefined;
  }
  return {
    callId: content.call_id,
    outcome: outcome as CallTerminalOutcome,
    initiator: content.initiator,
    startedTs: content.started_ts,
    ...(answeredTs === undefined ? {} : { answeredTs }),
    endedTs: content.ended_ts,
    ...(durationSec === undefined ? {} : { durationSec }),
  };
};

export const isValidCallRecordWriter = (
  record: CallTerminalRecord,
  senderId: string,
  participants?: CallRoomParticipants,
) => {
  if (participants) {
    const { localUserId, peerUserId } = participants;
    if (
      !matrixIdPattern.test(localUserId) ||
      !peerUserId ||
      !matrixIdPattern.test(peerUserId) ||
      peerUserId === localUserId ||
      (record.initiator !== localUserId && record.initiator !== peerUserId) ||
      (senderId !== localUserId && senderId !== peerUserId)
    )
      return false;
    return isTerminalWriter(
      record.outcome,
      senderId === record.initiator ? 'initiator' : 'receiver',
    );
  }
  if (!matrixIdPattern.test(senderId)) return false;
  return isTerminalWriter(
    record.outcome,
    senderId === record.initiator ? 'initiator' : 'receiver',
  );
};

export const parseCallRecordEvent = (
  content: unknown,
  senderId: string,
  originServerTs: number,
  participants?: CallRoomParticipants,
) => {
  const record = parseCallRecordContent(content);
  return record &&
    isFiniteNonNegative(originServerTs) &&
    isValidCallRecordWriter(record, senderId, participants) &&
    originServerTs + callClockSkewToleranceMs >= record.endedTs
    ? record
    : undefined;
};

export const buildCallRecordContent = (record: CallTerminalRecord) => {
  const parsed = parseCallRecordContent({
    call_id: record.callId,
    outcome: record.outcome,
    initiator: record.initiator,
    started_ts: record.startedTs,
    answered_ts: record.answeredTs,
    ended_ts: record.endedTs,
    duration_sec: record.durationSec,
  });
  if (!parsed) throw new Error('Invalid call record');
  return {
    call_id: parsed.callId,
    outcome: parsed.outcome,
    initiator: parsed.initiator,
    started_ts: parsed.startedTs,
    ...(parsed.answeredTs === undefined
      ? {}
      : { answered_ts: parsed.answeredTs }),
    ended_ts: parsed.endedTs,
    ...(parsed.durationSec === undefined
      ? {}
      : { duration_sec: parsed.durationSec }),
  };
};

export const parseCallProtocolEvent = (
  event: MatrixCallEventInput,
  options: ParseOptions,
): CallProtocolEvent | null => {
  if (
    !options.directRoomIds.has(event.roomId) ||
    !event.eventId ||
    !matrixIdPattern.test(event.senderId) ||
    !isFiniteNonNegative(event.originServerTs)
  ) {
    return null;
  }
  if (event.type === 'm.call.notify') {
    if (!isRecord(event.content)) return null;
    const mentions = event.content['m.mentions'];
    if (
      event.content.application !== 'm.call' ||
      event.content.notify_type !== 'ring' ||
      !isCallId(event.content.call_id) ||
      !isRecord(mentions) ||
      mentions.room !== false ||
      !Array.isArray(mentions.user_ids) ||
      mentions.user_ids.length !== 1 ||
      typeof mentions.user_ids[0] !== 'string' ||
      !matrixIdPattern.test(mentions.user_ids[0]) ||
      event.originServerTs - options.now > callClockSkewToleranceMs ||
      options.now - event.originServerTs > 30_000
    ) {
      return null;
    }
    return {
      kind: 'ring',
      roomId: event.roomId,
      eventId: event.eventId,
      senderId: event.senderId,
      originServerTs: event.originServerTs,
      callId: event.content.call_id,
      peerId: mentions.user_ids[0],
    };
  }
  if (event.type === 'org.gba.call') {
    const record = parseCallRecordEvent(
      event.content,
      event.senderId,
      event.originServerTs,
    );
    return record &&
      event.originServerTs - options.now <= callClockSkewToleranceMs
      ? {
          kind: 'terminal',
          roomId: event.roomId,
          eventId: event.eventId,
          senderId: event.senderId,
          originServerTs: event.originServerTs,
          record,
        }
      : null;
  }
  return null;
};
